import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import moment from 'moment'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import cn from 'clsx'
import styled from 'styled-components'
import GeoContext from 'contexts/geoContext'
import OfferCalendarMonthPicker from './MonthPicker/OfferCalendarMonthPicker'
import OfferCalendarDayPicker from './DayPicker/OfferCalendarDayPicker'
import { rem } from 'polished'
import { OfferPackageOptionView, canBookNow } from 'selectors/offerPage/offerPageSelectors'
import { getInteractionStudioDataFromOfferPage } from 'selectors/interactionStudioSelectors'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions, OfferPageFilterTypes } from 'contexts/OfferPage/offerPageStateReducer'
import OfferCalendarNotAvailableMessage from './OfferCalendarNotAvailableMessage'
import OfferCalendarNoFlightsMessage from './OfferCalendarNoFlightsMessage'
import { CALENDAR_GA_EVENT_ACTION, CALENDAR_GA_EVENT_LABEL } from 'constants/analytics'
import CloseButton from 'components/Common/CloseButton'
import OfferCalendarDurationSelect from './CalendarDuration/OfferCalendarDurationSelect'
import OfferCalendarRoomSelect from './CalendarRooms/OfferCalendarRoomSelect'
import useHasMounted from 'hooks/useHasMounted'
import UserAnalyticsActionContext from 'contexts/userAnalyticsActionContext'

import CalendarPickerRecommendations from './Recommendations/CalendarPickerRecommendations'
import CalendarPickerRecommendationsHeading from './Recommendations/CalendarPickerRecommendationsHeading'
import { shouldEnableCalendarRecommendations } from 'selectors/featuresSelectors'
import IconButton from 'components/Luxkit/Button/IconButton'
import {
  isMultipleRoomBookingActive,
  getCalendarMonths,
  getSelectedOfferPageDay,
  isFetchingCalendars,
  getDaysOtherAvailability,
  getPackagesForFilters,
  isAnyFlightsActive,
} from 'selectors/multipleRoomBookingSelectors'
import { sumUpOccupancies } from 'lib/offer/occupancyUtils'
import Group from 'components/utils/Group'
import OfferCalendarDatePickerFooter from './OfferCalendarDatePickerFooter'
import { EmptyArray, nonNullable } from 'lib/array/arrayUtils'
import Heading from 'components/Luxkit/Typography/Heading'
import OfferCalendarFlightsToggle from './OfferCalendarFlightsToggle'
import LineArrowLeftIcon from 'components/Luxkit/Icons/line/LineArrowLeftIcon'
import Pane from 'components/Common/Pane'
import OfferCalendarCapacitySelect from './CalendarCapacity/OfferCalendarCapacitySelect'
import { isVillaOffer } from 'lib/offer/offerTypes'
import config from 'constants/config'
import { isLuxuryPlusEnabled } from 'loyalty/selectors/featureToggle'
import { OFFER_TYPE_HOTEL } from 'constants/offer'
import OfferCalendarLuxuryPlusToggle from 'loyalty/components/OfferCalendarLuxuryPlusToggle'

const Calendar = styled.div`
  padding: ${rem(24)};
  flex-grow: 1;
  min-height: ${rem(400)};
`

const Header = styled(Pane)`
  position: sticky;
  top: 0;
  display: flex;
  flex-direction: column;
  padding: ${rem(24)};
  padding-bottom: ${rem(16)};
  z-index: 2;
  border-bottom: 1px solid ${props => props.theme.palette.neutral.six};
  background-color: ${props => props.theme.palette.neutral.eight};
`

const RecommendationHeadingWrapper = styled.div`
  flex-grow: 1;
  display: flex;
  justify-content: flex-start;
  align-items: center;
`

interface Props {
  offer: App.Offer;
  airport: App.Airport;
  checkIn?: moment.Moment;
  fetching?: boolean;
  selectedDay?: App.CalendarDay;
  flights?: boolean;
  calendarError?: boolean;
  calendarAvailability: boolean;
  useRooms?: boolean;
  defaultDayPicker?: boolean;
  defaultMonth?: string;
  duration: number;
  relevantPackages: Array<OfferPackageOptionView>;
  selectedPackageView?: OfferPackageOptionView;
  recommendationsShown: boolean;
  rooms?: App.Occupants[];
  otherAvailability: { [checkIn: string]: boolean };
  selectedPackage: App.Package,
  interactionStudioData?: App.ISACheckoutData;
  filter: OfferPageFilterTypes;
  isAnyFlightsActive: boolean;
  isMultipleRoomBookingActive: boolean;
  subFilters?: Array<OfferPageFilterTypes>;
  scrollRef: React.RefObject<HTMLDivElement>;
  canBookNow?: boolean;
  isLuxuryPlusEnabled: boolean;
}

const defaultSubFilters = ['duration', 'airport', 'room']
function OfferCalendarDatePicker(props: Props) {
  const {
    checkIn,
    offer,
    fetching,
    selectedDay,
    flights,
    calendarError,
    calendarAvailability,
    defaultMonth,
    defaultDayPicker,
    duration,
    relevantPackages,
    selectedPackageView,
    otherAvailability,
    rooms = EmptyArray,
    recommendationsShown,
    interactionStudioData,
    filter,
    isAnyFlightsActive,
    isMultipleRoomBookingActive,
    subFilters = defaultSubFilters,
    scrollRef,
    canBookNow,
    isLuxuryPlusEnabled,
  } = props

  const { currentRegionHasFlights } = useContext(GeoContext)
  const onUserAnalyticsAction = useContext(UserAnalyticsActionContext)
  const [monthIndex, setMonth] = useState<string | undefined>(defaultMonth ?? selectedDay?.monthKey)
  const dispatch = useOfferPageDispatch()
  const mounted = useHasMounted()

  useEffect(() => {
    if (!fetching && checkIn && selectedDay && !selectedDay.hotelAvailable && !otherAvailability[selectedDay.checkIn]) {
      // clear check in if the date we have selected is no longer available
      dispatch({ type: OfferPageActions.setCheckInDate, checkIn: undefined })
    }
  }, [checkIn, selectedDay]) // eslint-disable-line react-hooks/exhaustive-deps

  const onDropdownClose = useCallback(() => {
    onUserAnalyticsAction?.(CALENDAR_GA_EVENT_ACTION.CloseFilter, CALENDAR_GA_EVENT_LABEL.CloseCross)
    dispatch({ type: OfferPageActions.closeFilter })
  }, [dispatch, onUserAnalyticsAction])

  const onBackClick = useCallback(() => {
    if (recommendationsShown) {
      dispatch({ type: OfferPageActions.hideRecommendationsView })
      return
    }

    setMonth(undefined)
  }, [setMonth, dispatch, recommendationsShown])

  const onBackToMonth = useCallback(() => {
    if (recommendationsShown) {
      dispatch({ type: OfferPageActions.hideRecommendationsView })
      return
    }
    dispatch({ type: OfferPageActions.resetDates })
    onUserAnalyticsAction?.(CALENDAR_GA_EVENT_ACTION.YearView)
    setMonth(undefined)
  }, [dispatch, onUserAnalyticsAction, recommendationsShown])

  const occupancyNumber = useMemo(() => sumUpOccupancies(rooms), [rooms])

  const flightsEnabled = currentRegionHasFlights && offer.offerFlightsEnabled && !offer.bundledWithFlightsOnly && occupancyNumber < 10 && config.businessTraveller.currentAccountMode !== 'business'

  const hasSelectedMonth = monthIndex !== undefined

  const capacities = useMemo(() => nonNullable(offer.packages.flatMap(pkg => pkg.roomRate?.capacities)), [offer])

  const anyPackageHasMemberPrice = useMemo(() => offer.packages.some(pkg => pkg.memberPrice > 0), [offer.packages])
  useEffect(() => {
    if (anyPackageHasMemberPrice && isLuxuryPlusEnabled) {
      dispatch({ type: OfferPageActions.setDisplayMemberPrice, displayMemberPrice: true })
    }
  }, [anyPackageHasMemberPrice, dispatch, isLuxuryPlusEnabled])

  return (<>
    <Header>
      <Group
        direction="horizontal"
        horizontalAlign="space-between"
        verticalAlign="center"
        gap={16}
      >
        {(hasSelectedMonth || recommendationsShown) && <IconButton
          kind="tertiary"
          className={cn({ visible: hasSelectedMonth || recommendationsShown })}
          outdent="left"
          size="small"
          onClick={onBackClick}
          aria-label="Back"
        >
          <LineArrowLeftIcon />
        </IconButton>}
        {!recommendationsShown && <Group
          verticalAlign="center"
          fullWidth
          direction="horizontal"
          horizontalAlign="space-between"
          wrap="wrap"
          gap={4}
        >
          <Group direction="vertical" gap={4}>
            <Heading variant="heading3">
              {hasSelectedMonth && 'Select check-in date'}
              {!hasSelectedMonth && 'Select check-in month'}
            </Heading>

          </Group>
          <Group direction="horizontal" gap={8} wrap="wrap">
            {subFilters.includes('capacity') && <OfferCalendarCapacitySelect capacities={capacities} disableMultiRoom={isVillaOffer(offer)} />}
            {!isMultipleRoomBookingActive && subFilters.includes('room') && <OfferCalendarRoomSelect />}
            {subFilters.includes('duration') && <OfferCalendarDurationSelect />}
            {flightsEnabled && subFilters.includes('airport') && <OfferCalendarFlightsToggle />}
            {isLuxuryPlusEnabled && offer.type === OFFER_TYPE_HOTEL && anyPackageHasMemberPrice && <OfferCalendarLuxuryPlusToggle />}
          </Group>
        </Group>}
        {recommendationsShown && <RecommendationHeadingWrapper>
          <CalendarPickerRecommendationsHeading />
        </RecommendationHeadingWrapper>}
        <CloseButton size="small" onClick={onDropdownClose} />
      </Group>
    </Header>
    <Calendar>
      {recommendationsShown && (<CalendarPickerRecommendations
        offerId={offer.id}
        offerType={offer.type}
        rooms={rooms}
        heading={`Similar hotels to ${offer.property?.name ?? offer.location}`}
        seedOffer={offer}
      />)}
      {!recommendationsShown && (
        <>
          {mounted && !fetching && !calendarAvailability && <>
            {(calendarError || !flights) && <OfferCalendarNotAvailableMessage hasError={calendarError} />}
            {!calendarError && flights && <OfferCalendarNoFlightsMessage canSwitchToHotel={!offer?.bundledWithFlightsOnly} />}
          </>}
          {calendarAvailability && <>
            {monthIndex !== undefined && !fetching && <OfferCalendarDayPicker
              onBackToMonth={onBackToMonth}
              initialMonth={monthIndex}
              defaultDayPicker={defaultDayPicker}
              flightsOnly={offer.bundledWithFlightsOnly}
              contentRef={scrollRef}
            />}
            {monthIndex === undefined && <OfferCalendarMonthPicker
              onMonthSelect={setMonth}
            />}
          </>
          }
        </>
      )}
    </Calendar>
    <OfferCalendarDatePickerFooter
      show={!!selectedDay && !recommendationsShown}
      canBook={canBookNow}
      offer={offer}
      selectedDay={selectedDay}
      duration={duration}
      relevantPackages={relevantPackages}
      selectedPackageView={selectedPackageView}
      interactionStudioData={interactionStudioData}
      filter={filter}
      isAnyFlightsActive={isAnyFlightsActive}
    />
  </>
  )
}

export default withOfferPageAndReduxState((state, appState) => {
  return {
    checkIn: state.checkInDate,
    offer: state.offer,
    airport: state.airport,
    flights: state.flights,
    duration: state.duration,
    recommendationsShown: state.showRecommendationsView && shouldEnableCalendarRecommendations(appState, state.offer),
    relevantPackages: getPackagesForFilters(state, appState),
    selectedPackageView: state.selectedPackageView,
    rooms: state.rooms,
    otherAvailability: getDaysOtherAvailability(state, appState),
    selectedDay: getSelectedOfferPageDay(state, appState),
    fetching: isFetchingCalendars(state, appState),
    calendarAvailability: getCalendarMonths(state, appState).availableCount > 0,
    calendarError: !!state.offer?.packages.some(pkg => appState.calendar.calendarErrors[pkg.uniqueKey]),
    selectedPackage: state.package,
    interactionStudioData: getInteractionStudioDataFromOfferPage(appState, state),
    filter: state.activeFilter,
    isAnyFlightsActive: isAnyFlightsActive(state, appState),
    canBookNow: canBookNow(state, appState),
    isMultipleRoomBookingActive: isMultipleRoomBookingActive(state, appState),
    isLuxuryPlusEnabled: isLuxuryPlusEnabled(appState),
  }
})(OfferCalendarDatePicker)
